import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../../components/AppIcon';

const PrivacyPolicyPage = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - Intime ATS | Your Data Protection Rights</title>
        <meta name="description" content="Read Intime's Privacy Policy to understand how we collect, use, and protect your personal information while using our applicant tracking system." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://intime.com/legal/privacy" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="border-b border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-white" />
                </div>
                <span className="text-2xl font-bold text-gray-900">Intime</span>
              </Link>
              
              <nav className="flex items-center space-x-6">
                <Link to="/" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Home
                </Link>
                <Link to="/pricing" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Pricing
                </Link>
                <Link to="/careers" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Careers
                </Link>
                <Link to="/login" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">
                  Sign In
                </Link>
              </nav>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-600">
              Last updated: December 17, 2024
            </p>
          </div>

          <div className="prose prose-lg prose-gray max-w-none">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <div className="flex items-start">
                <Icon name="Info" size={20} className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    Our Commitment to Privacy
                  </h3>
                  <p className="text-blue-800 text-base">
                    At Intime, we are committed to protecting your privacy and ensuring the security of your personal information. 
                    This Privacy Policy explains how we collect, use, and safeguard your data when you use our applicant tracking system.
                  </p>
                </div>
              </div>
            </div>

            {/* Placeholder content sections */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Information We Collect
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="FileText" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will detail the types of information we collect from users and candidates.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                How We Use Your Information
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="Settings" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will explain how we use collected information to provide and improve our services.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Data Sharing and Disclosure
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="Share2" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will outline when and how we may share your information with third parties.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Data Security
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="Shield" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will describe the security measures we implement to protect your data.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Your Rights and Choices
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="User" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will detail your rights regarding your personal data and how to exercise them.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Cookie Policy
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="Cookie" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will explain our use of cookies and similar tracking technologies.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                International Data Transfers
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="Globe" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will address how we handle data transfers across international borders.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Changes to This Policy
              </h2>
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Icon name="RefreshCw" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-4">
                  Content will be added here
                </p>
                <p className="text-sm text-gray-500">
                  This section will explain how we handle updates to our privacy policy.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">
                Contact Us
              </h2>
              <p className="text-blue-800 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2 text-blue-800">
                <p><strong>Email:</strong> privacy@intime.com</p>
                <p><strong>Address:</strong> [Company Address]</p>
                <p><strong>Phone:</strong> [Company Phone]</p>
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <Link to="/" className="flex items-center space-x-2 mb-4 md:mb-0">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-white" />
                </div>
                <span className="text-2xl font-bold">Intime</span>
              </Link>
              
              <div className="flex items-center space-x-6 text-sm text-gray-400">
                <Link to="/legal/privacy" className="text-white">
                  Privacy Policy
                </Link>
                <Link to="/legal/terms" className="hover:text-white transition-colors">
                  Terms of Service
                </Link>
                <span>&copy; 2024 Intime. All rights reserved.</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;